import React, { useState, useEffect, useMemo } from 'react';
import { parse, format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';


const MailDetails = () => {
  const [mailDetails, setMailDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Custom date parsing function for multiple formats
  const parseCustomDate = (dateString) => {
    let parsedDate;
    // Try parsing multiple possible formats
    try {
      parsedDate = parse(dateString, 'dd-MM-yyyy HH:mm:ss', new Date());
      if (isNaN(parsedDate)) {
        // If the date is not parsed correctly, try another format
        parsedDate = parse(dateString, 'MM/dd/yyyy hh:mm:ss a', new Date());
      }
    } catch (e) {
      console.error('Error parsing date:', e);
      return null;
    }
    return parsedDate;
  };

  const formatCustomDate = (date) => {
    return format(date, 'dd/MM/yyyy hh:mm a'); // Format as required (DD/MM/YYYY hh:mm:ss AM/PM)
  };

  const BUId = sessionStorage.getItem("BUId");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/Mailer/GetAllMailDetails?BUId=${BUId}`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        // Sort in descending order using custom date parsing
        const sortedData = data.sort((a, b) =>
          parseCustomDate(b.sendDate)?.getTime() - parseCustomDate(a.sendDate)?.getTime()
        );
        setMailDetails(sortedData);
      } catch (error) {
        console.error('Error fetching mail details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Filter and search functionality
  const filteredMails = useMemo(() => {
    // Normalize search term to lowercase
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return mailDetails.filter(mail => {
      // Check if any part of the search term matches email details
      // const matchesReceiver = mail.matchesReceiver.some(email =>
      //   email.toLowerCase().includes(normalizedSearchTerm)
      // );
      const matchesSender = mail.fromEmail.toLowerCase().includes(normalizedSearchTerm);
      const matchesMailType = mail.mailType.toLowerCase().includes(normalizedSearchTerm);

      // Date-wise search
      const matchesDate = mail.sendDate.toLowerCase().includes(normalizedSearchTerm);

      return  matchesSender || matchesMailType || matchesDate;
    });
  }, [mailDetails, searchTerm]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMails.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  // Calculate total pages
  const totalPages = Math.ceil(filteredMails.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="container mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-200 p-4">
          <h1 className="text-xl ">Mail Details</h1>
        </div>

        {/* Search Input */}
        <div className="p-4 bg-gray-100 border-b">
          <div className="max-w-md ml-auto relative">

            <input
              type="text"
              placeholder="Search..."
              className="w-full p-3 pl-10 text-gray-700 bg-white rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 absolute left-3 top-3 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-purple-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Business Unit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Campaign Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Template Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Receiver</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Sender</th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Mail Type</th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Send Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((mail, index) => (
                  <tr key={index} className="hover:bg-gray-100 transition duration-200">
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.buName}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.campaignName}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.templateName}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.toEmail}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.fromEmail}</td>
                    {/* <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.mailType}</td> */}
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                      {formatCustomDate(parseCustomDate(mail.sendDate))}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm">
                      <button
                        className="bg-transparent hover:bg-blue-100 text-blue-500 font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out transform hover:scale-105"
                        onClick={() => {
                          const mailLogId = mail.mailLogId;
                          window.location.href = `/viewmaildetails?maillogid=${mailLogId}`;
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} className="text-gray-700 hover:text-blue-500" />
                      </button>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No mail details available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-100 px-4 py-3 flex items-center justify-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => paginate(index + 1)}
                className={`px-4 py-2 text-sm font-medium rounded-lg border 
                  ${currentPage === index + 1
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {index + 1}
              </button>
            ))}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailDetails;
